import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useAppLoader } from "src/_common/hooks/actions/common/appLoaderHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { CANDIDATE_STATUS } from "src/_config";
import PositionTarget from "./PositionTarget/PositionTarget";
//import TalentScouting from "./TalentScouting";
import Screening from "./Screening/Screening";
import Interview from "./Interview/Interview";
import FinalDecision from "./FinalDecision/FinalDecision";
// import { TalentScouting } from "../talent-scouting";
import TalentScouting from "./TalentScouting/TalentScouting";
import SetValidityModal from "./Screening/SetValidityModal";
import { useJobListSelector } from "src/_common/hooks/selectors/jobSelector.";
interface JobDetailsMainProps {}

export default function JobDetailsMain({}: JobDetailsMainProps) {
  const { t } = useTranslation("job-details");
  const { jobId } = useParams<{ jobId: string }>();
  const [activeTab, setActiveTab] = useState("talent-scouting");
  const [selectedCandidateForInterview, setSelectedCandidateForInterview] = useState<number | null>(null);
  const [selectedCandidateForFinalDecision, setSelectedCandidateForFinalDecision] = useState<string | null>(null);
  const jobListings = useJobListSelector();
  const currentJob = jobListings?.find((job) => job.emp_job_id == jobId);

  // Page state for each tab to maintain pagination
  const [screeningCurrentPage, setScreeningCurrentPage] = useState(1);
  const [interviewCurrentPage, setInterviewCurrentPage] = useState(1);
  const [finalDecisionCurrentPage, setFinalDecisionCurrentPage] = useState(1);

  // Set Validity Modal state
  const [isSetValidityModalOpen, setIsSetValidityModalOpen] = useState(false);
  const [pendingCandidateForInterview, setPendingCandidateForInterview] = useState<{
    candidateId: any;
    currentPage?: number;
    isAIInterview?: boolean; // Flag to indicate if this is for AI Interview
    aiInterviewData?: any; // Store AI interview specific data
  } | null>(null);

  const { showLoader, hideLoader } = useAppLoader();
  const { callUpdateValidity, callScheduleRequestAI } = useInterviewApi();
  const screeningApi = useScreeningApi();
  const toast = useToaster();

  // Tab configuration
  const tabs = [
    {
      id: "position-target",
      label: t("tabs.positionTarget.label"),
      icon: "fa-bullseye",
      component: PositionTarget,
    },
    {
      id: "talent-scouting",
      label: t("tabs.talentScouting.label"),
      icon: "fa-search",
      component: TalentScouting,
      badge: null,
    },
    {
      id: "screening",
      label: t("tabs.screening.label"),
      icon: "fa-filter",
      component: Screening,
      badge: "2",
    },
    {
      id: "interview",
      label: t("tabs.interview.label"),
      icon: "fa-users",
      component: Interview,
    },
    {
      id: "final-decision",
      label: t("tabs.finalDecision.label"),
      icon: "fa-check-circle",
      component: FinalDecision,
      badge: "1",
    },
  ];

  useEffect(() => {
    if (jobId) {
      showLoader();
      setActiveTab("talent-scouting");
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  }, [jobId]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Clear selected candidate when switching tabs (except when going to interview or final-decision)
    if (tabId !== "interview") {
      setSelectedCandidateForInterview(null);
    }
    if (tabId !== "final-decision") {
      setSelectedCandidateForFinalDecision(null);
    }
  };

  const handleScheduleInterview = (candidateId: any, currentPage?: number) => {
    // Store the pending candidate and open the validity modal
    setPendingCandidateForInterview({ candidateId, currentPage, isAIInterview: false });
    setIsSetValidityModalOpen(true);
  };

  const handleSendAIInterview = (candidateId: any, currentPage?: number, aiInterviewData?: any) => {
    // Store the pending candidate for AI Interview and open the validity modal
    setPendingCandidateForInterview({
      candidateId,
      currentPage,
      isAIInterview: true,
      aiInterviewData,
    });
    setIsSetValidityModalOpen(true);
  };

  const handleValiditySubmit = (hours: number) => {
    console.log("Validity hours set:", hours);

    if (!pendingCandidateForInterview) {
      return;
    }

    const payload = {
      screening_id: pendingCandidateForInterview.candidateId,
      validity_time: hours,
    };

    callUpdateValidity(
      payload,
      (message: string, resp: any) => {
        console.log("Update validity success:", message, resp);

        // Check if this is for AI Interview or Manual Interview
        if (pendingCandidateForInterview.isAIInterview) {
          // Execute AI Interview logic after validity is set
          const aiData = pendingCandidateForInterview.aiInterviewData;

          if (aiData && aiData.screeningId) {
            const aiPayload = {
              job_id: jobId as any,
              screening_id: aiData.screeningId,
            } as any;

            callScheduleRequestAI(
              aiPayload,
              (aiMessage: string, aiResp: any) => {
                toast.success(t("jobDetails.screening.aiInterviewSent"));

                // Add feedback for AI interview sent
                const feedbackPayload = {
                  screening_id: aiData.screeningId,
                  application_type: 1,
                  status: 9,
                  title: t("jobDetails.screening.apiPayload.aiInterviewTitle") || "AI Interview Sent",
                  description:
                    t("jobDetails.screening.apiPayload.aiInterviewDescription") ||
                    "AI interview has been sent to the candidate.",
                };

                screeningApi.callAddFeedback(
                  feedbackPayload,
                  (feedbackMessage: string, feedbackResp: any) => {
                    console.log("AI Interview feedback recorded successfully:", feedbackResp);

                    // Update candidate status to AI Interview
                    screeningApi.callUpdateStatus(
                      {
                        screening_id: aiData.screeningId,
                        status: CANDIDATE_STATUS.SEND_AI_INTERVIEW,
                      },
                      (statusMessage: string, statusResp: any) => {
                        console.log("Candidate status updated to AI Interview:", statusResp);

                        // Navigate to interview tab
                        setSelectedCandidateForInterview(pendingCandidateForInterview.candidateId);
                        if (pendingCandidateForInterview.currentPage) {
                          setInterviewCurrentPage(pendingCandidateForInterview.currentPage);
                        }
                        setActiveTab("interview");
                        setPendingCandidateForInterview(null);
                      },
                      (statusError: string) => {
                        toast.error(t("jobDetails.screening.statusUpdateError") + ": " + statusError);
                      }
                    );
                  },
                  (feedbackError: string) => {
                    console.error("Error recording AI Interview feedback:", feedbackError);
                    // Continue with navigation even if feedback fails
                    setSelectedCandidateForInterview(pendingCandidateForInterview.candidateId);
                    if (pendingCandidateForInterview.currentPage) {
                      setInterviewCurrentPage(pendingCandidateForInterview.currentPage);
                    }
                    setActiveTab("interview");
                    setPendingCandidateForInterview(null);
                  }
                );
              },
              (aiError: string) => {
                toast.error(t("jobDetails.screening.aiInterviewError") + ": " + aiError);
                setPendingCandidateForInterview(null);
              }
            );
          }
        } else {
          // For Manual Interview: Navigate to interview tab
          if (pendingCandidateForInterview) {
            setSelectedCandidateForInterview(pendingCandidateForInterview.candidateId);

            // Set the interview page to show the candidate
            if (pendingCandidateForInterview.currentPage) {
              setInterviewCurrentPage(pendingCandidateForInterview.currentPage);
            }

            // Navigate to interview tab
            setActiveTab("interview");

            // Reset pending candidate
            setPendingCandidateForInterview(null);
          }
        }
      },
      (error: string) => {
        console.error("Update validity error:", error);
        toast.error("Failed to set validity: " + error);
        setPendingCandidateForInterview(null);
      }
    );
  };

  const handleValidityModalClose = () => {
    setIsSetValidityModalOpen(false);
    setPendingCandidateForInterview(null);
  };

  const handleMoveToFinalDecision = (candidateId: string, currentPage?: number) => {
    setSelectedCandidateForFinalDecision(candidateId);
    // Set the final decision page to show the candidate
    if (currentPage) {
      setFinalDecisionCurrentPage(currentPage);
    }
    setActiveTab("final-decision");
  };

  const handleMoveToInterview = (candidateId: any, currentPage?: number) => {
    // Navigate to interview tab after manual interview scheduling
    setSelectedCandidateForInterview(candidateId);
    if (currentPage) {
      setInterviewCurrentPage(currentPage);
    }
    setActiveTab("interview");
  };

  const renderActiveComponent = () => {
    const activeTabConfig = tabs.find((tab) => tab.id === activeTab);
    if (!activeTabConfig) return null;

    const Component = activeTabConfig.component;

    // Pass additional props based on the active tab
    const componentProps: any = { jobId };

    if (activeTab === "screening") {
      componentProps.onScheduleInterview = handleScheduleInterview;
      componentProps.onSendAIInterview = handleSendAIInterview;
      componentProps.onMoveToInterview = handleMoveToInterview;
      componentProps.currentPage = screeningCurrentPage;
      componentProps.onPageChange = setScreeningCurrentPage;
    } else if (activeTab === "interview") {
      componentProps.selectedCandidateId = selectedCandidateForInterview;
      componentProps.onMoveToFinalDecision = handleMoveToFinalDecision;
      componentProps.currentPage = interviewCurrentPage;
      componentProps.onPageChange = setInterviewCurrentPage;
      console.log("Parent: Passing selectedCandidateId to Interview:", selectedCandidateForInterview);
    } else if (activeTab === "final-decision") {
      componentProps.selectedCandidateId = selectedCandidateForFinalDecision;
      componentProps.currentPage = finalDecisionCurrentPage;
      componentProps.onPageChange = setFinalDecisionCurrentPage;
      console.log("Parent: Passing selectedCandidateId to FinalDecision:", selectedCandidateForFinalDecision);
    }

    return <Component {...componentProps} />;
  };

  return (
    <div className="dashboard-main">
      {/* Navigation Tabs */}
      <div className="job-details-nav">
        <div className="nav-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <i className={`fa ${tab.icon} tab-icon`}></i>
              <span className="tab-label">{tab.label}</span>
              {/* {tab.badge && <span className="tab-badge">{tab.badge}</span>} */}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="d-block">{renderActiveComponent()}</div>

      {/* Set Validity Modal */}
      <SetValidityModal
        isOpen={isSetValidityModalOpen}
        onClose={handleValidityModalClose}
        onSubmit={handleValiditySubmit}
      />
    </div>
  );
}
